import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const SHOP_LINKS = [
  { to: "/shop?category=tactical-gear", label: "Tactical Gear" },
  { to: "/shop?category=clothing", label: "Clothing" },
  { to: "/shop?category=personal-security", label: "Personal Security" },
  { to: "/shop?category=preparedness", label: "Preparedness" },
  { to: "/shop?category=outdoor-survival", label: "Outdoor & Survival" },
  { to: "/shop?category=everyday-carry", label: "Everyday Carry" },
  { to: "/shop?category=home-security", label: "Home Security" },
  { to: "/shop?category=footwear", label: "Footwear" },
  { to: "/kits", label: "Ready Kits" },
  { to: "/shop?filter=new", label: "New Arrivals" },
  { to: "/shop?filter=sale", label: "Sale" },
];

export const Navbar = ({ onOpenAdvisor }) => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setAnnouncement(data.announcement)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false); setMobileOpen(false); setQuery("");
    }
  };

  return (
    <header className="sticky top-0 z-50">
      <div data-testid="announcement-bar" className="bg-[#121212] text-white text-[11px] tracking-[0.2em] font-bold uppercase text-center py-2 px-4">
        {announcement}
      </div>
      <nav className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase">
            <img src="/logo.png" alt="Shodan Security logo" className="h-12 w-12 object-contain" />
            <span>SHODAN <span className="text-[#4A5D23]">SECURITY</span></span>
          </Link>
          <div className="hidden lg:flex items-center gap-7 text-[13px] font-semibold uppercase tracking-wider">
            <div className="relative" onMouseEnter={() => setShopOpen(true)} onMouseLeave={() => setShopOpen(false)}>
              <button data-testid="nav-shop" className="flex items-center gap-1 py-5 hover:text-[#4A5D23] transition-colors duration-150">
                Shop <ChevronDown size={14} />
              </button>
              {shopOpen && (
                <div className="absolute left-0 top-full bg-white border border-black/10 w-56 py-2 shadow-[4px_4px_0px_rgba(0,0,0,0.9)]">
                  {SHOP_LINKS.map((l) => (
                    <Link key={l.label} to={l.to} onClick={() => setShopOpen(false)} className="block px-4 py-2 text-xs hover:bg-[#F4F3F0] hover:text-[#4A5D23] transition-colors duration-150">
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <button data-testid="nav-advisor" onClick={onOpenAdvisor} className="hover:text-[#4A5D23] transition-colors duration-150">Build Your Kit</button>
            <Link data-testid="nav-fieldguide" to="/field-guide" className="hover:text-[#4A5D23] transition-colors duration-150">Field Guide</Link>
            <Link data-testid="nav-club" to="/club" className="hover:text-[#4A5D23] transition-colors duration-150">Club</Link>
            <Link data-testid="nav-events" to="/events" className="hover:text-[#4A5D23] transition-colors duration-150">Events</Link>
            <Link data-testid="nav-contact" to="/contact" className="hover:text-[#4A5D23] transition-colors duration-150">Contact</Link>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="nav-search-btn" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" className="hover:text-[#4A5D23] transition-colors duration-150"><Search size={20} /></button>
            {user ? (
              <div className="hidden md:flex items-center gap-3">
                <Link data-testid="nav-account" to={user.role === "admin" ? "/admin" : "/account"} className="hover:text-[#4A5D23] transition-colors duration-150"><User size={20} /></Link>
                <button data-testid="nav-logout" onClick={logout} className="text-[11px] uppercase font-bold tracking-wider hover:text-[#4A5D23]">Logout</button>
              </div>
            ) : (
              <Link data-testid="nav-login" to="/login" aria-label="Account" className="hidden md:block hover:text-[#4A5D23] transition-colors duration-150"><User size={20} /></Link>
            )}
            <Link data-testid="nav-cart" to="/cart" className="relative hover:text-[#4A5D23] transition-colors duration-150" aria-label="Cart">
              <ShoppingCart size={20} />
              {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#4A5D23] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center">{count}</span>}
            </Link>
            <button data-testid="nav-mobile-toggle" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {searchOpen && (
          <form onSubmit={submitSearch} className="border-t border-black/10 bg-[#F4F3F0] px-4 py-3">
            <div className="max-w-7xl mx-auto flex gap-2">
              <input data-testid="nav-search-input" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder='Try "camping backpack" or "vehicle emergency"'
                className="flex-1 border border-black/20 px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
              <button data-testid="nav-search-submit" type="submit" className="bg-[#121212] text-white px-6 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Search</button>
            </div>
          </form>
        )}
        {mobileOpen && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-black/10 bg-white px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
            {SHOP_LINKS.map((l) => (
              <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">{l.label}</Link>
            ))}
            <button onClick={() => { setMobileOpen(false); onOpenAdvisor(); }} className="block py-2 text-sm font-semibold uppercase tracking-wider text-[#4A5D23]">Build Your Kit</button>
            <Link to="/field-guide" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">Field Guide</Link>
            <Link to="/club" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">Club</Link>
            <Link to="/events" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">Events</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">Contact</Link>
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">My Account</Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="block py-2 text-sm font-semibold uppercase tracking-wider">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-semibold uppercase tracking-wider">Sign In</Link>
            )}
          </div>
        )}
      </nav>
    </header>
  );
};
